import { datadogLogs } from '@datadog/browser-logs';
import { useEffect, useState } from 'react';

/**
 * The slides CDN serves decks only to a browser holding its signed cookies. The
 * egress browser has no session to get them from /self, so the page trades the
 * LiveKit recorder token in its link for them before any deck iframe loads.
 *
 * Bounded: past the timeout the deck loads anyway, so a slow lms never leaves a
 * recording without its slides for longer than that.
 */
const TIMEOUT_MS = 5000;

let exchange: Promise<void> | null = null;

/**
 * The link's token, when it is one lms minted for a recorder. Decode only; lms verifies it.
 * Dyte recordings have no LiveKit token, so lms adds a `slidesCdnToken` for them instead.
 */
function recorderTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('slidesCdnToken') || params.get('livekitToken');
  if (!token) return null;
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(part + '='.repeat((4 - (part.length % 4)) % 4)));
    const metadata = typeof payload.metadata === 'string' ? JSON.parse(payload.metadata) : {};
    return payload.video?.recorder === true || metadata?.type === 'recorder' ? token : null;
  } catch {
    return null;
  }
}

/** One exchange per page load, shared by every deck frame on it. */
function exchangeRecorderToken(token: string): Promise<void> {
  if (exchange) return exchange;
  const request = fetch(`${import.meta.env.VITE_BACKEND_URL || ''}/api/v2/slides-cdn/recorder-cookies`, {
    method: 'POST',
    credentials: 'include',
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => {
      if (!res.ok) datadogLogs.logger.warn('Slides CDN cookie exchange refused', { label: 'slides-cdn', status: res.status });
    })
    .catch((error: unknown) => {
      datadogLogs.logger.warn('Slides CDN cookie exchange failed', { label: 'slides-cdn' }, error as Error);
    });
  exchange = new Promise((resolve) => {
    const timer = setTimeout(resolve, TIMEOUT_MS);
    request.finally(() => {
      clearTimeout(timer);
      resolve();
    });
  });
  return exchange;
}

/** False until a deck iframe may load: the cookies are set, the exchange gave up, or there is no recorder token. */
export function useSlidesCdnCookies(): boolean {
  const [token] = useState(recorderTokenFromUrl);
  const [ready, setReady] = useState(!token);

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    exchangeRecorderToken(token).then(() => {
      if (active) setReady(true);
    });
    return () => {
      active = false;
    };
  }, [token]);

  return ready;
}
